import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import heroBg from "../assets/hero-01.png";

const categories = ["All", "AC HVAC Systems", "AC Maintenance", "AC Repair", "AC Tips"];

const posts = [
  { id: 1,  category: "AC HVAC Systems", title: "AC Troubleshooting: Common Problems and Quick Solutions",       excerpt: "Learn how HVAC technicians diagnose and repair common air conditioning problems, from refrigerant leaks to electrical faults, and how regular servicing helps prevent unexpected breakdowns.", date: "May 12, 2026", readTime: "5 min read" },
  { id: 2,  category: "AC HVAC Systems", title: "Understanding Commercial Rooftop HVAC Systems",                 excerpt: "Rooftop air conditioning systems are widely used in offices, malls, and commercial buildings. Discover how they work and why they are ideal for large-scale cooling.",                     date: "May 8, 2026",  readTime: "6 min read" },
  { id: 3,  category: "AC HVAC Systems", title: "Professional AC Installation: Why It Matters",                  excerpt: "Proper installation plays a major role in system performance and energy efficiency. See why professional HVAC installation ensures reliable cooling and longer equipment lifespan.",       date: "Apr 30, 2026", readTime: "4 min read" },
  { id: 4,  category: "AC Maintenance",  title: "How Often Should You Service Your Air Conditioner?",           excerpt: "Most homeowners underestimate the importance of regular AC servicing. We break down a simple maintenance schedule that keeps your unit running efficiently all year.",                   date: "Apr 22, 2026", readTime: "4 min read" },
  { id: 5,  category: "AC Maintenance",  title: "5 Signs Your AC Filter Needs Immediate Replacement",           excerpt: "A clogged filter is one of the most common causes of poor AC performance. Learn the warning signs and how a simple filter change can dramatically improve your system.",               date: "Apr 15, 2026", readTime: "3 min read" },
  { id: 6,  category: "AC Maintenance",  title: "The True Cost of Skipping Annual HVAC Maintenance",            excerpt: "Skipping your annual service might save money short-term, but the long-term costs tell a very different story. Here's what neglected maintenance actually costs you.",                  date: "Apr 2, 2026",  readTime: "5 min read" },
  { id: 7,  category: "AC Maintenance",  title: "How to Clean Your AC Unit at Home Safely",                     excerpt: "Basic AC upkeep between professional service visits can extend your unit's life significantly. Follow our step-by-step guide for safe home cleaning of split AC units.",               date: "Mar 28, 2026", readTime: "5 min read" },
  { id: 8,  category: "AC Repair",       title: "Why Is My AC Blowing Warm Air? Causes & Fixes",                excerpt: "An AC blowing warm air is one of the most frustrating faults. We walk through the most common causes — from low refrigerant to faulty compressors — and what to do.",                  date: "Mar 20, 2026", readTime: "5 min read" },
  { id: 9,  category: "AC Repair",       title: "AC Making Strange Noises? Here's What Each Sound Means",       excerpt: "Rattling, hissing, banging — your AC communicates through sound. Learn what each unusual noise indicates and when it's time to call a qualified HVAC technician.",                   date: "Mar 10, 2026", readTime: "4 min read" },
  { id: 10, category: "AC Repair",       title: "Refrigerant Leaks: Detection, Risks, and Repair",              excerpt: "Refrigerant leaks are more serious than most people realise. They harm system performance, drive up electricity bills, and pose environmental risks if left unaddressed.",           date: "Feb 28, 2026", readTime: "6 min read" },
  { id: 11, category: "AC Tips",         title: "How to Reduce Your Electricity Bill with Inverter AC",          excerpt: "Inverter air conditioners can cut cooling costs by up to 40%. Understand how inverter technology works and why it pays for itself over time in energy savings.",                     date: "Feb 18, 2026", readTime: "4 min read" },
  { id: 12, category: "AC Tips",         title: "Best Temperature Settings for Comfort and Efficiency",          excerpt: "The temperature you set your AC to has a massive impact on your electricity bill. Discover the optimal settings for different times of day and building types in Uganda.",           date: "Feb 5, 2026",  readTime: "3 min read" },
  { id: 13, category: "AC Tips",         title: "Choosing the Right AC Size for Your Room",                      excerpt: "An undersized AC struggles to cool, and an oversized one short-cycles and wastes energy. Learn how to correctly size an air conditioner for any room or space.",                   date: "Jan 25, 2026", readTime: "5 min read" },
  { id: 14, category: "AC Tips",         title: "Split AC vs Cassette AC: Which Is Right for You?",              excerpt: "Both split and cassette air conditioners have their place. We compare installation requirements, airflow coverage, aesthetics, and cost to help you decide.",                       date: "Jan 12, 2026", readTime: "5 min read" },
  { id: 15, category: "AC HVAC Systems", title: "What Is a VRF System and When Do You Need One?",                excerpt: "Variable Refrigerant Flow systems are the gold standard for large commercial buildings. Learn how they work, what they cost, and when they make sense for your project.",            date: "Jan 5, 2026",  readTime: "7 min read" },
];

const PAGE_SIZE = 3;

const categoryCounts = categories.slice(1).reduce((acc, cat) => {
  acc[cat] = posts.filter((p) => p.category === cat).length;
  return acc;
}, {});

const categoryBg = {
  "AC HVAC Systems": "#0F1030",
  "AC Maintenance":  "#0a3d62",
  "AC Repair":       "#1a1a2e",
  "AC Tips":         "#0d2137",
};

// Post card slides in from left, staggered by index
function PostCard({ post, index }) {
  return (
    <motion.div
      className="bg-white flex flex-col sm:flex-row overflow-hidden"
      style={{ border: "1.5px solid #e5e7eb" }}
      initial={{ opacity: 0, x: -70 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.15 }}
    >
      {/* Image placeholder */}
      <div
        className="w-full sm:w-44 lg:w-48 flex-shrink-0 min-h-[160px] sm:min-h-0 flex items-center justify-center"
        style={{ backgroundColor: categoryBg[post.category] ?? "#0F1030" }}
      >
        <div className="text-center px-3">
          <p className="text-xs font-bold tracking-widest uppercase" style={{ color: "#00C7EF" }}>
            {post.category}
          </p>
          <div className="w-8 h-0.5 mx-auto mt-2" style={{ backgroundColor: "#00C7EF" }} />
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col justify-between flex-1">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs font-bold px-2.5 py-0.5" style={{ backgroundColor: "#00C7EF", color: "#0F1030" }}>
              {post.category}
            </span>
            <span className="text-xs text-gray-400">{post.date} · {post.readTime}</span>
          </div>
          <h2 className="font-extrabold text-base sm:text-lg leading-snug mb-2" style={{ color: "#0F1030" }}>
            {post.title}
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">{post.excerpt}</p>
        </div>
        <div className="mt-4">
          <button
            className="text-xs sm:text-sm font-bold px-5 py-2 tracking-wide transition-colors duration-150"
            style={{ backgroundColor: "#0F1030", color: "#ffffff" }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#00C7EF"; e.currentTarget.style.color = "#0F1030"; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#0F1030"; e.currentTarget.style.color = "#ffffff"; }}
          >
            Learn More
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [page, setPage] = useState(1);

  const filtered = activeCategory === "All"
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCategory = (cat) => {
    setActiveCategory(cat === activeCategory ? "All" : cat);
    setPage(1);
  };

  return (
    <motion.div
      className="font-poppins"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >

      {/* ── HERO ── */}
      <section className="relative min-h-[300px] sm:min-h-[380px] lg:min-h-[450px] flex items-center justify-center text-white overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 199, 239, 0.55)" }} />
        <div className="relative text-center px-4 sm:px-8 lg:px-16 py-14 sm:py-20">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.8)" }}>
            SMARTCOOL Blog
          </h1>
          <p className="text-base sm:text-lg font-semibold uppercase tracking-widest max-w-2xl mx-auto text-white leading-snug" style={{ textShadow: "1px 1px 6px rgba(0,0,0,0.9)" }}>
            HVAC Insights, Tips & Industry Knowledge
          </p>
        </div>
      </section>

      {/* ── BLOG BODY ── */}
      <section className="bg-gray-50 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* ── POSTS ── */}
            <div className="lg:col-span-2 flex flex-col gap-5">

              {/* key changes on page or category — re-triggers all card animations */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${activeCategory}-${page}`}
                  className="flex flex-col gap-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {visible.map((post, i) => (
                    <PostCard key={post.id} post={post} index={i} />
                  ))}
                </motion.div>
              </AnimatePresence>

              {/* ── PAGINATION ── */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1 pt-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className="w-9 h-9 text-sm font-bold transition-colors duration-150"
                      style={{
                        backgroundColor: page === p ? "#0F1030" : "transparent",
                        color: page === p ? "#fff" : "#0F1030",
                        border: page === p ? "2px solid #0F1030" : "2px solid transparent",
                        textDecoration: page !== p ? "underline" : "none",
                      }}
                    >
                      {p}
                    </button>
                  ))}
                  {page < totalPages && (
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      className="ml-1 px-3 h-9 text-sm font-bold transition-colors duration-150"
                      style={{ color: "#0F1030" }}
                    >
                      →
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ── SIDEBAR — slides in from right ── */}
            <motion.div
              className="lg:col-span-1 flex flex-col gap-5"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
            >
              {/* Blog Categories */}
              <div style={{ border: "2px solid #0F1030" }}>
                <div className="px-5 py-3" style={{ backgroundColor: "#0F1030" }}>
                  <p className="text-sm font-extrabold tracking-widest uppercase" style={{ color: "#00C7EF" }}>
                    Blog Categories
                  </p>
                </div>
                <div className="bg-white">
                  {categories.slice(1).map((cat, i) => (
                    <motion.button
                      key={cat}
                      onClick={() => handleCategory(cat)}
                      className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium transition-colors duration-150 border-b last:border-b-0"
                      style={{
                        borderBottomColor: "#f3f4f6",
                        backgroundColor: activeCategory === cat ? "#f0fbff" : "transparent",
                        color: "#374151",
                      }}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.35, ease: "easeOut", delay: 0.3 + i * 0.08 }}
                    >
                      <span className={activeCategory === cat ? "font-bold" : ""}>{cat}</span>
                      <span className="text-xs font-bold tabular-nums" style={{ color: "#6b7280" }}>
                        ({String(categoryCounts[cat]).padStart(2, "0")})
                      </span>
                    </motion.button>
                  ))}
                  {activeCategory !== "All" && (
                    <button
                      onClick={() => handleCategory("All")}
                      className="w-full px-5 py-3 text-xs font-bold tracking-wide text-center"
                      style={{ backgroundColor: "#0F1030", color: "#00C7EF" }}
                    >
                      VIEW ALL POSTS
                    </button>
                  )}
                </div>
              </div>

              {/* CTA — scales in */}
              <motion.div
                className="py-8 px-5 text-center"
                style={{ backgroundColor: "#0F1030", border: "2px solid #00C7EF" }}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: 0.55 }}
              >
                <p className="text-white text-sm font-medium mb-1">Need HVAC help?</p>
                <h3 className="text-lg font-extrabold mb-4" style={{ color: "#00C7EF" }}>Talk to Our Engineers</h3>
                <a
                  href="/contact"
                  className="inline-block font-bold text-sm px-6 py-2.5 tracking-wide transition-colors duration-150"
                  style={{ backgroundColor: "#00C7EF", color: "#0F1030" }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#ffffff"; e.currentTarget.style.color = "#0F1030"; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#00C7EF"; e.currentTarget.style.color = "#0F1030"; }}
                >
                  CONTACT US
                </a>
              </motion.div>

            </motion.div>
          </div>
        </div>
      </section>

    </motion.div>
  );
}